import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { subscribeRefreshTokenRevoked } from "@/features/auth/refreshTokenRevoked"
import {
  dispatchSessionExpiredRedirect,
  SESSION_EXPIRED_FLAG,
  SESSION_EXPIRED_RETURN_URL,
} from "@/features/auth/sessionExpired"
import { tokenStorage } from "@/features/auth/tokenStorage"
import {
  getApplicationConfiguration,
  readCurrentUserIdFromApplicationConfiguration,
  readCurrentUserRolesFromApplicationConfiguration,
  readGrantedPoliciesFromApplicationConfiguration,
  readHasManageableEvents,
} from "@/lib/api/appConfigApi"
import type { TokenResponse } from "@/lib/api/http"

export const LOGOUT_REDIRECT_FLAG = "auth:redirect-home-after-logout"

export type LogoutReason = "manual" | "sessionExpired"

type LogoutOptions = {
  reason?: LogoutReason
}

type AuthContextValue = {
  accessToken: string | null
  isAuthenticated: boolean
  setSession: (tokens: TokenResponse) => void
  logout: (options?: LogoutOptions) => void
  grantedPolicies: Record<string, boolean> | null
  currentUserId: string | null
  isAdmin: boolean
  hasManageableEvents: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [accessToken, setAccessToken] = useState<string | null>(() => tokenStorage.getAccessToken())
  const [grantedPolicies, setGrantedPolicies] = useState<Record<string, boolean> | null>(null)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [hasManageableEvents, setHasManageableEvents] = useState(false)

  const setSession = useCallback((tokens: TokenResponse) => {
    tokenStorage.setTokens(tokens.access_token, tokens.refresh_token ?? null)
    setAccessToken(tokens.access_token)
  }, [])

  const logout = useCallback((options?: LogoutOptions) => {
    const reason = options?.reason ?? "manual"
    if (reason === "sessionExpired") {
      const currentPath = `${window.location.pathname}${window.location.search}`
      sessionStorage.setItem(SESSION_EXPIRED_FLAG, "1")
      if (!currentPath.startsWith("/login")) {
        sessionStorage.setItem(SESSION_EXPIRED_RETURN_URL, currentPath)
      }
      sessionStorage.removeItem(LOGOUT_REDIRECT_FLAG)
      dispatchSessionExpiredRedirect()
    } else {
      sessionStorage.setItem(LOGOUT_REDIRECT_FLAG, "1")
      sessionStorage.removeItem(SESSION_EXPIRED_RETURN_URL)
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG)
    }
    tokenStorage.clear()
    setAccessToken(null)
    setGrantedPolicies(null)
    setCurrentUserId(null)
    setIsAdmin(false)
    setHasManageableEvents(false)
  }, [])

  useEffect(() => {
    return subscribeRefreshTokenRevoked(() => {
      logout({ reason: "sessionExpired" })
    })
  }, [logout])

  useEffect(() => {
    if (!accessToken) {
      setGrantedPolicies(null)
      setCurrentUserId(null)
      setIsAdmin(false)
      setHasManageableEvents(false)
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        const cfg = await getApplicationConfiguration(accessToken)
        if (cancelled) return
        const cfgUnknown = cfg as unknown
        setGrantedPolicies(readGrantedPoliciesFromApplicationConfiguration(cfgUnknown))
        setCurrentUserId(readCurrentUserIdFromApplicationConfiguration(cfgUnknown))
        const roles = readCurrentUserRolesFromApplicationConfiguration(cfgUnknown)
        setIsAdmin(Boolean(roles?.includes("admin")))
        setHasManageableEvents(readHasManageableEvents(cfgUnknown))
      } catch {
        if (!cancelled) {
          /** Пустой объект = политики «разрешены», но ни одна не true; иначе гарды с `!== null` висят вечным лоадером. */
          setGrantedPolicies({})
          setCurrentUserId(null)
          setIsAdmin(false)
          setHasManageableEvents(false)
        }
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [accessToken])

  const value = useMemo(
    () => ({
      accessToken,
      isAuthenticated: Boolean(accessToken),
      setSession,
      logout,
      grantedPolicies,
      currentUserId,
      isAdmin,
      hasManageableEvents,
    }),
    [accessToken, setSession, logout, grantedPolicies, currentUserId, isAdmin, hasManageableEvents],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/* useAuth — отдельный экспорт для потребителей; хук не компонент */
// eslint-disable-next-line react-refresh/only-export-components -- useAuth is a hook, not a component
export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider")
  }
  return ctx
}
